import { useQuery } from '@tanstack/react-query'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import {
  ServerCog, RefreshCw, AlertCircle, AlertTriangle, CheckCircle2, Clock, Loader2,
} from 'lucide-react'
import { getWorkerHealth, getWorkerRuns } from '../../lib/api'

// ── Status presentation ───────────────────────────────────────────────────────
// Mirrors the Overview health card: ok=green, running=blue, stale=amber,
// error=red, never_run=grey. Driven entirely by the backend-derived status.
const STATUS_META = {
  ok:        { dot: 'bg-emerald-500',            text: 'text-emerald-700', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'OK'      },
  running:   { dot: 'bg-blue-500 animate-pulse', text: 'text-blue-700',    badge: 'bg-blue-50 text-blue-700 border-blue-200',          label: 'Running' },
  stale:     { dot: 'bg-amber-500',              text: 'text-amber-700',   badge: 'bg-amber-50 text-amber-700 border-amber-200',       label: 'Stale'   },
  error:     { dot: 'bg-red-500',                text: 'text-red-700',     badge: 'bg-red-50 text-red-700 border-red-200',             label: 'Error'   },
  never_run: { dot: 'bg-slate-300',              text: 'text-slate-400',   badge: 'bg-slate-50 text-slate-500 border-slate-200',       label: 'No data' },
}

function timeAgo(iso) {
  if (!iso) return '—'
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true }) } catch { return '—' }
}
function fmtTime(iso) {
  if (!iso) return '—'
  try { return format(parseISO(iso), 'dd MMM HH:mm:ss') } catch { return '—' }
}
function fmtInterval(s) {
  if (!s) return '—'
  if (s % 3600 === 0) return `${s / 3600}h`
  if (s % 60 === 0) return `${s / 60}m`
  return `${s}s`
}
function fmtDuration(a, b) {
  if (!a || !b) return null
  try {
    const sec = Math.round((parseISO(b) - parseISO(a)) / 1000)
    if (sec < 0) return null
    return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`
  } catch { return null }
}

export default function DataManagement() {
  const {
    data: health, isLoading: healthLoading, refetch: refetchHealth, isFetching: healthFetching,
  } = useQuery({
    queryKey: ['sync', 'worker-health'],
    queryFn: getWorkerHealth,
    refetchInterval: 30_000,
  })
  const { data: runsData, refetch: refetchRuns } = useQuery({
    queryKey: ['sync', 'worker-runs'],
    queryFn: () => getWorkerRuns({ limit: 150 }),
    refetchInterval: 30_000,
  })

  const jobs = health?.jobs ?? []
  const runs = runsData?.runs ?? []
  const tableMissing =
    (health && health.table_present === false) ||
    (runsData && runsData.table_present === false)

  // worker_name → friendly label, for the run-history table
  const labelByName = Object.fromEntries(jobs.map(j => [j.name, j.label]))

  const errored = jobs.filter(j => j.status === 'error')
  const degraded = jobs.filter(j => j.status === 'stale' || j.status === 'never_run')

  const overall = jobs.length === 0
    ? { cls: 'bg-slate-50 border-slate-200 text-slate-500',     Icon: Clock,        msg: 'Waiting for data…' }
    : errored.length > 0
      ? { cls: 'bg-red-50 border-red-200 text-red-700',         Icon: AlertCircle,  msg: `${errored.length} job${errored.length !== 1 ? 's' : ''} failing` }
      : degraded.length > 0
        ? { cls: 'bg-amber-50 border-amber-200 text-amber-700', Icon: AlertTriangle, msg: `${degraded.length} job${degraded.length !== 1 ? 's' : ''} need attention` }
        : { cls: 'bg-emerald-50 border-emerald-200 text-emerald-700', Icon: CheckCircle2, msg: 'All systems live' }
  const OverallIcon = overall.Icon

  function refreshAll() { refetchHealth(); refetchRuns() }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
            <ServerCog size={20} className="text-slate-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Data Management</h1>
            <p className="text-xs text-slate-400">
              Background sync &amp; ingest health. Pulls run automatically every few minutes — no manual sync needed.
            </p>
          </div>
        </div>
        <button onClick={refreshAll} className="btn-secondary text-xs py-1.5 px-3" disabled={healthFetching}>
          {healthFetching ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          Refresh
        </button>
      </div>

      {tableMissing && (
        <div className="card p-4 text-sm text-amber-700 bg-amber-50 border border-amber-200">
          worker_runs table not found — run the 2026-05-17-worker-runs migration.
        </div>
      )}

      {/* Overall banner */}
      <div className={`rounded-xl border px-4 py-3 flex items-center gap-2 text-sm font-medium ${overall.cls}`}>
        <OverallIcon size={16} />
        {overall.msg}
        {health?.checked_at && (
          <span className="ml-auto text-xs font-normal opacity-70">checked {timeAgo(health.checked_at)}</span>
        )}
      </div>

      {/* Worker status grid */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Workers</h2>
        {healthLoading ? (
          <div className="skeleton h-24 rounded" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {jobs.map(j => {
              const m = STATUS_META[j.status] ?? STATUS_META.never_run
              return (
                <div key={j.name} className="card p-3.5">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-sm font-medium text-slate-700 truncate">{j.label}</span>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-full border ${m.badge}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
                      {m.label}
                    </span>
                  </div>
                  <dl className="space-y-1 text-xs text-slate-500">
                    <div className="flex justify-between gap-2"><dt>Last run</dt><dd className="text-slate-700 truncate">{timeAgo(j.last_run)}</dd></div>
                    <div className="flex justify-between gap-2"><dt>Last success</dt><dd className="text-slate-700 truncate">{timeAgo(j.last_success)}</dd></div>
                    <div className="flex justify-between gap-2"><dt>Interval</dt><dd className="text-slate-700">{fmtInterval(j.poll_interval_seconds)}</dd></div>
                  </dl>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Needs attention — full error messages */}
      {(errored.length > 0 || degraded.length > 0) && (
        <div>
          <h2 className="text-sm font-semibold text-slate-700 mb-2">Needs attention</h2>
          <div className="space-y-2">
            {[...errored, ...degraded].map(j => {
              const m = STATUS_META[j.status] ?? STATUS_META.never_run
              const isErr = j.status === 'error'
              return (
                <div key={j.name} className={`rounded-lg border p-3 ${isErr ? 'bg-red-50/60 border-red-200' : 'bg-amber-50/60 border-amber-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${m.dot}`} />
                    <span className="text-sm font-semibold text-slate-800">{j.label}</span>
                    <span className={`text-[11px] font-semibold ${m.text}`}>{m.label}</span>
                    <span className="ml-auto text-xs text-slate-400">last run {timeAgo(j.last_run)}</span>
                  </div>
                  {j.error_message ? (
                    <p className="text-xs text-slate-600 font-mono break-words whitespace-pre-wrap pl-4">{j.error_message}</p>
                  ) : (
                    <p className="text-xs text-slate-500 pl-4">
                      {j.status === 'stale'
                        ? 'No run within ~3× its interval — the worker may be down.'
                        : j.status === 'never_run'
                          ? 'No heartbeat recorded yet — the worker may not have started.'
                          : 'Failed — no message recorded.'}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Recent run history */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Recent runs</h2>
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Worker</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Started</th>
                  <th className="px-3 py-2 font-medium">Duration</th>
                  <th className="px-3 py-2 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {runs.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No runs recorded yet.</td></tr>
                ) : runs.map(r => {
                  const m = STATUS_META[r.status] ?? STATUS_META.never_run
                  const label = labelByName[r.worker_name] || r.worker_name
                  const dur = fmtDuration(r.started_at, r.completed_at)
                  const stats = [
                    r.messages_seen ? `${r.messages_seen} seen` : null,
                    r.parsed ? `${r.parsed} parsed` : null,
                    r.duplicate ? `${r.duplicate} dup` : null,
                    r.rejected ? `${r.rejected} rejected` : null,
                  ].filter(Boolean).join(' · ')
                  return (
                    <tr key={r.id} className={r.status === 'error' ? 'bg-red-50/40' : ''}>
                      <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{label}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 font-medium ${m.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />{m.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap" title={fmtTime(r.started_at)}>{timeAgo(r.started_at)}</td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{dur || (r.status === 'running' ? '…' : '—')}</td>
                      <td className="px-3 py-2 text-slate-500 max-w-md truncate" title={r.error_message || stats}>
                        {r.error_message
                          ? <span className="text-red-600 font-mono">{r.error_message}</span>
                          : (stats || '—')}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
